/**
 * Lightweight analytics beacon. Fire-and-forget — never blocks UI, never
 * throws upward. Uses `navigator.sendBeacon` when available so events
 * survive page unload / tab close during the "Send My Signal" flow.
 *
 * Events fired from the conversion layer:
 *   - biaas_pricing_view   (once per session when pricing enters viewport)
 *   - biaas_form_submit    (successful lead capture)
 *   - biaas_form_error     (server or validation error on submit)
 */
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${API_BASE}/api/analytics/event`;
const SESSION_KEY = "eigoo_analytics_fired";

function firedSet() {
    try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        return new Set(raw ? JSON.parse(raw) : []);
    } catch {
        return new Set();
    }
}

function markFired(event) {
    try {
        const s = firedSet();
        s.add(event);
        sessionStorage.setItem(SESSION_KEY, JSON.stringify([...s]));
    } catch {
        /* storage disabled — treat every fire as fresh */
    }
}

/**
 * Fire an event. `options.once` collapses duplicate fires within one tab
 * session (used for the pricing view).
 */
export function track(event, meta = {}, options = {}) {
    if (!event) return;
    if (options.once && firedSet().has(event)) return;
    const payload = JSON.stringify({ event, meta });
    try {
        if (typeof navigator !== "undefined" && navigator.sendBeacon) {
            const blob = new Blob([payload], { type: "application/json" });
            navigator.sendBeacon(ENDPOINT, blob);
        } else {
            fetch(ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: payload,
                keepalive: true,
            }).catch(() => {});
        }
        if (options.once) markFired(event);
    } catch {
        /* beacons are non-critical */
    }
}
